import { Injectable } from '@angular/core';
import { Observable, catchError, finalize, tap, throwError } from 'rxjs';
import { AcodisCategoryDTO, AcodisWorkflowClient, AcodisWorkflowDTO, AcodisWorkflowLimitedDTO, AcodisWorkflowTestResult, FilterDTO, ImportType, SortDirection } from 'src/app/base/models/models.swagger.generated';
import { ToastService } from 'src/app/base/services/toast.service';
import { ShowInactiveService } from 'src/app/base/shared/grid-base/show-inactive.service';
import { IAdaptorService } from 'src/app/base/shared/syncfusion/adaptor-service.interface';
import { IPagedItems } from 'src/app/base/shared/syncfusion/paged-items.interface';
import { SpinnerService } from 'src/app/base/spinner/spinner.service';
import { ErrorType } from 'src/app/core/routing/error-types';

@Injectable(
  { providedIn: 'root' }
)
export class AcodisWorkflowService extends ShowInactiveService implements IAdaptorService {

  constructor(
    private acodisWorkflowClient: AcodisWorkflowClient,
    private toastService: ToastService,
    private spinnerService: SpinnerService
  ) {
    super();
  }

  public getAllAcodisWorkflowsLimited(importType: ImportType): Observable<AcodisWorkflowLimitedDTO[]> {
    return this.acodisWorkflowClient.getAllAcodisWorkflows(importType)
      .pipe(
        catchError((error: any) => {
          if (!this.toastService.doesErrorExistAlready(ErrorType.serverError)) {
            this.toastService.showError('i18n.errors.server-error.title', 'i18n.errors.server-error.message', null, ErrorType.serverError);
          }
          return throwError(() => error);
        })
      );
  }

  public getData(skip: number, take: number, filters: FilterDTO[], sortBy: string, sortDirection: SortDirection): Observable<IPagedItems> {
    if (!sortBy) {
      sortBy = 'Name';
    }
    if (!filters) {
      filters = [];
    }
    this.spinnerService.show();
    return this.acodisWorkflowClient.searchAcodisWorkflows(skip, take, sortBy, sortDirection, filters)
      .pipe(
        finalize(() => {
          this.spinnerService.hide();
        }),
        catchError((error: any) => {
          if (!this.toastService.doesErrorExistAlready(ErrorType.serverError)) {
            this.toastService.showError('i18n.errors.server-error.title', 'i18n.errors.server-error.message', null, ErrorType.serverError);
          }
          return throwError(() => error);
        })
      )
  }

  public addRecord(data: AcodisWorkflowDTO): Observable<AcodisWorkflowDTO> {
    this.spinnerService.show();
    return this.acodisWorkflowClient.createAcodisWorkflow(data)
      .pipe(
        finalize(() => {
          this.spinnerService.hide();
        }),
        tap(() => {
          this.toastService.showSuccess(
            'i18n.domain.import-configuration.acodis-workflows.messages.create.success-title',
            'i18n.domain.import-configuration.acodis-workflows.messages.create.success-message');
        }),
        catchError((error: any) => {
          if (error.status === 409) {
            this.toastService.showError(
              'i18n.domain.import-configuration.acodis-workflows.messages.create.duplicate-title',
              'i18n.domain.import-configuration.acodis-workflows.messages.create.duplicate-message');
          } else {
            if (!this.toastService.doesErrorExistAlready(ErrorType.serverError)) {
              this.toastService.showError('i18n.domain.import-configuration.acodis-workflows.messages.create.fail-title', 'i18n.domain.import-configuration.acodis-workflows.messages.create.fail-message', null, ErrorType.serverError);
            }
          }
          return throwError(() => error);
        })
      );
  }

  public updateRecord(dto: AcodisWorkflowDTO): Observable<AcodisWorkflowDTO> {
    this.spinnerService.show();
    return this.acodisWorkflowClient.updateAcodisWorkflow(dto)
      .pipe(
        finalize(() => {
          this.spinnerService.hide();
        }),
        tap(() => {
          this.toastService.showSuccess(
            'i18n.domain.import-configuration.acodis-workflows.messages.update.success-title',
            'i18n.domain.import-configuration.acodis-workflows.messages.update.success-message');
        }),
        catchError((error: any) => {
          if (error.status === 409) {
            this.toastService.showError(
              'i18n.domain.import-configuration.acodis-workflows.messages.update.duplicate-title',
              'i18n.domain.import-configuration.acodis-workflows.messages.update.duplicate-message');
          } else {
            if (!this.toastService.doesErrorExistAlready(ErrorType.serverError)) {
              this.toastService.showError('i18n.domain.import-configuration.acodis-workflows.messages.update.fail-title', 'i18n.domain.import-configuration.acodis-workflows.messages.update.fail-message', null, ErrorType.serverError);
            }
          }
          return throwError(() => error);
        })
      );
  }

  public deleteRecord(id: number): Observable<void> {
    this.spinnerService.show();
    return this.acodisWorkflowClient.deleteAcodisWorkflow(id)
      .pipe(
        finalize(() => {
          this.spinnerService.hide();
        }),
        tap(() => {
          this.toastService.showSuccess(
            'i18n.domain.import-configuration.acodis-workflows.messages.delete.success-title',
            'i18n.domain.import-configuration.acodis-workflows.messages.delete.success-message');
        }),
        catchError((error: any) => {
          this.spinnerService.hide();
          if (error.status === 403) {
            this.toastService.showError(
              'i18n.domain.import-configuration.acodis-workflows.messages.delete.forbidden-title',
              'i18n.domain.import-configuration.acodis-workflows.messages.delete.forbidden-message');
          } else {
            if (!this.toastService.doesErrorExistAlready(ErrorType.serverError)) {
              this.toastService.showError('i18n.domain.import-configuration.acodis-workflows.messages.delete.fail-title', 'i18n.domain.import-configuration.acodis-workflows.messages.delete.fail-message', null, ErrorType.serverError);
            }
          }
          return throwError(() => error);
        })
      );
  }

  public getAcodisWorkflow(id: number): Observable<AcodisWorkflowDTO> {
    this.spinnerService.show();
    return this.acodisWorkflowClient.getAcodisWorkflow(id)
      .pipe(
        finalize(() => {
          this.spinnerService.hide();
        }),
        catchError((error: any) => {
          if (!this.toastService.doesErrorExistAlready(ErrorType.serverError)) {
            this.toastService.showError('i18n.errors.server-error.title', 'i18n.errors.server-error.message', null, ErrorType.serverError);
          }
          return throwError(() => error);
        })
      );
  }

  public getAcodisCategories(id: number): Observable<AcodisCategoryDTO[]> {
    if (!id) {
      return new Observable<AcodisCategoryDTO[]>();
    }
    this.spinnerService.show();
    return this.acodisWorkflowClient.getAcodisWorkflowCategories(id)
    .pipe(
      finalize(() => {
        this.spinnerService.hide();
      }),
      catchError((error: any) => {
        //Error handling in component
        return throwError(() => error);
      })
    );
  }

  public testAccodisWorkflow(dto: AcodisWorkflowDTO): Observable<AcodisWorkflowTestResult> {
    return this.acodisWorkflowClient.testAcodisWorkflow(dto)
    .pipe(
      catchError((error: any) => {
        if (!this.toastService.doesErrorExistAlready(ErrorType.serverError)) {
          this.toastService.showApiException(error, 'i18n.errors.server-error.title', 'i18n.errors.server-error.message');
        }
        return throwError(() => error);
      })
    );
  }
}
